import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { AlertController, ModalController } from '@ionic/angular';
import { NgForm } from '@angular/forms';

import {
  ActivityFormatService, AgeGroupService, ArrayService, AuthService, ChurchService, TheatrePerformanceService,
  DatabaseService, DocumentService, EmailService, FileService, ServiceYearMemberService, FestivalUserService   
} from '../../../core/services';

import { 
  ActivityFormat, TheatrePerformance, DocumentReference, ServiceYearMember, NameValue, FestivalUser 
} from '../../../core/models';
import { PageClass } from '../../../core/classes/page';
import { Activity, TheatreStatus } from '../../../core/enums';

@Component({
  selector: 'app-edit-theatre-performance-format',
  templateUrl: './edit-performance.component.html',
  styleUrls: ['./edit-performance.component.less', '../../../core/styles/common.less']
})

export class EditTheatrePerformanceComponent extends PageClass implements OnInit {
  
  activity: NameValue;
  activityList: NameValue[] = [];
  formatList: ActivityFormat[];
  performanceCategoryList: NameValue[] = [];
  locationList: NameValue[] = [];
  approverList: DocumentReference[] = [];
  statusList: NameValue[] = [];

  serviceYearMembers: DocumentReference[] = [];
  AllServiceYearMembers: ServiceYearMember[] = [];
  memberListPlaceHolder: string = 'Required';

  performanceCategoryObj: NameValue = null;
  formatObj: ActivityFormat = null;

  isLoading: boolean = true;
  
  isTitleDisabled: boolean = false;
  isMemberListDisabled: boolean = false;
  isApprovalDetailsDisabled: boolean = false;
  isFeeDetailsDisabled: boolean = false;
  isUploadDetailsDisabled: boolean = false;
  isConsentMessageDisabled: boolean = false;
  isExtraRolesDisabled: boolean = false;

  areMembersAccepted: boolean = true;
  isUploadingFile: boolean = false;
  isUploadedFileChanged: boolean = false;
  isOverDueDate: boolean = false;
  isAwaitingCommitteReview: boolean = false;

  submitLabel: string = 'Save';

  isFullAccessUser: boolean = false;

  @Input() performance: TheatrePerformance;
  
  @ViewChild('performanceForm', null) performanceForm: NgForm;

  constructor(
    protected activityFormatService: ActivityFormatService,
    protected ageGroupService: AgeGroupService,
    protected arrayService: ArrayService,
    protected authService: AuthService,
    protected theatrePerformanceService: TheatrePerformanceService,
    protected churchService: ChurchService,
    protected databaseService: DatabaseService,
    protected documentService: DocumentService,
    protected emailService: EmailService,
    protected fileService: FileService,
    protected serviceYearMemberService: ServiceYearMemberService,
    protected festivalUserService: FestivalUserService,

    protected alertController: AlertController,
    protected modalController: ModalController
  ) {
    super(ageGroupService, arrayService, authService, churchService, databaseService, festivalUserService);
  }

  protected loadData = async () => {
  
    if (this.currentUser.isActive && !this.currentUser.isDeleted) {
      this.isFullAccessUser = this.currentUser.isAdmin || this.currentUser.isActivityLeader;

      this.activity = this.activityFormatService.getActivityList().find(a => a.value === Activity.Theatre);
      this.performanceCategoryList = this.theatrePerformanceService.getPerformanceCategoryList();
      this.locationList = this.theatrePerformanceService.getLocationList();
      this.performanceCategoryObj = this.performanceCategoryList.find(t => t.value == this.performance.category.value);
      this.statusList = this.theatrePerformanceService.getPerformanceStatusList();

      this.isAwaitingCommitteReview = this.isFullAccessUser && this.performance.status.value == this.getStatusValue(TheatreStatus.PendingReview);

      this.onChurchSelected();
      this.onPerformanceCategorySelected();
    } else {
      this.dismissModal();
    }
  }

  public onChurchSelected = async () => {
    
    this.approverList = [];

    (await this.festivalUserService.getActivityApprovers(this.performance.church))
    .subscribe(approverList => {
      approverList
      .filter((a: FestivalUser) => a.activityList.findIndex(a => a.value === 'Theatre') > -1)
      .forEach((approver: FestivalUser) => {
          this.approverList.push(
              {
                  reference: this.databaseService.doc(`${this.festivalUserService.collection}/${approver.id}`).ref,
                  displayName: approver.user.displayName
              }
          );
      });
    });
  }

  public onPerformanceCategorySelected = (): void => {
   
    if (!this.isLoading) {
      this.clearData();
      this.performance.category = this.performanceCategoryObj;
    }

    this.activityFormatService.getFormatList(this.activity, this.performance.category)
        .subscribe(formatList => {
          this.formatList = formatList;
          this.formatObj = this.formatList.find(l => l.id === this.performance.format.reference.id);
          this.memberListPlaceHolder = this.formatObj.areMembersRequiredOnCreation ? 'Required' : '';

          if (!this.isFullAccessUser) {
            this.validateDueDates();
            this.isTitleDisabled = this.performance.isApprovalConfirmed || this.isPastDate(this.formatObj.creationDueDate);
            this.isMemberListDisabled = this.formatObj.areMembersRequiredOnCreation || this.isPastDate(this.formatObj.membersDueDate);
            this.isApprovalDetailsDisabled = this.performance.isApprovalConfirmed || this.isPastDate(this.formatObj.approvalDueDate);
            this.isFeeDetailsDisabled = this.performance.isPaymentConfirmed || this.isPastDate(this.formatObj.feeDueDate);
            this.isUploadDetailsDisabled = (this.performance.status.value == this.getStatusValue(TheatreStatus.SubmissionCompleted)) ||
              (!this.performance.fileName && this.formatObj.hasFileUpload && this.isPastDate(this.formatObj.uploadDueDate) && 
              this.performance.status.value != this.getStatusValue(TheatreStatus.ScriptReviewRequired));
            this.isConsentMessageDisabled = !this.isFullAccessUser && this.isPastDate(this.formatObj.creationDueDate);
            this.isExtraRolesDisabled = !this.isFullAccessUser && this.isPastDate(this.formatObj.performanceRolesDueDate);
          }
          
          if (this.isLoading)
            this.getMemberGroupList();
        });
  }

  private validateDueDates = (): void => {
    if (this.performance.status.value == this.getStatusValue(TheatreStatus.PendingCoordinatorApproval) && this.isPastDate(this.formatObj.approvalDueDate)) {
      this.showErrorMessage(
        'Cannot Edit Performance', 
        `Sorry, you cannot edit this performance, you already passed the approval due date ${new Date(this.formatObj.approvalDueDate).toDateString()}`, false);
      this.isOverDueDate = true;
    } 
    
    if (this.performance.status.value == this.getStatusValue(TheatreStatus.PendingPayment) && this.isPastDate(this.formatObj.feeDueDate)) { 
      this.showErrorMessage(
        'Cannot Edit Performance', 
        `Sorry, you cannot edit this performance, you already passed the payment due date ${new Date(this.formatObj.feeDueDate).toDateString()}`, false);
      this.isOverDueDate = true;
    }

    if (this.performance.status.value == this.getStatusValue(TheatreStatus.PendingScript) && this.isPastDate(this.formatObj.uploadDueDate)) { 
      this.showErrorMessage(
        'Cannot Edit Performance', 
        `Sorry, you cannot edit this performance, you already passed the ${this.formatObj.fileUploadLabel} upload due date ${new Date(this.formatObj.uploadDueDate).toDateString()}`, false);
      this.isOverDueDate = true;
    }

    if (this.performance.status.value == this.getStatusValue(TheatreStatus.ScriptReviewRequired) && this.isPastDate(this.formatObj.resubmissionDueDate)) { 
      this.showErrorMessage(
        'Cannot Edit Performance', 
        `Sorry, you cannot edit this performance, you already passed the resubmission due date ${new Date(this.formatObj.resubmissionDueDate).toDateString()}`, false);
      this.isOverDueDate = true;
    }
  }

  public onFormatSelected = () => {
    this.performance.format = {
      displayName: `${this.formatObj.displayName}`, 
      reference: this.databaseService.doc(`${this.activityFormatService.collection}/${this.formatObj.id}`).ref
    }

    if (new Date(this.formatObj.creationStartDate) > this.today)
      this.showErrorMessage('Cannot Create Performance',
        `Sorry you cannot create ${this.performance.category.name} performance until 
        ${new Date(this.formatObj.creationStartDate).toDateString()}`, true
      );

    if (this.isPastDate(this.formatObj.creationDueDate))
      this.showErrorMessage('Cannot Create Performance',
        `Sorry you cannot create ${this.performance.category.name} performance after 
        ${new Date(this.formatObj.creationDueDate).toDateString()}`, true
      );

      this.getMemberGroupList();
  }

  private showErrorMessage = async (header: string, errorMessage: string, clearData: boolean) => {
    const alert = await this.alertController.create({
      header: header,
      message: errorMessage,
      buttons: ['OK'],
    });

    alert.onDidDismiss().then(() => { 
      if (clearData)
        this.clearData();
    });

    await alert.present();
  }

  private clearData = (): void => {    
    this.formatObj = null;
    this.AllServiceYearMembers = [];
    this.serviceYearMembers = [];

    this.performance.category = null
    this.performance.format = null;
    this.performance.memberList = [];
  }

  private getMemberGroupList() {

    if (!this.performance.church || 
        !this.performance.category || 
        !this.performance.format) {
      return;
    }

    if (!this.isLoading) {
      this.serviceYearMembers = [];
      this.performance.memberList = [];
    }

    this.serviceYearMemberService.getTeamMembersYearLevels(
      this.performance.church, 
      this.formatObj.yearLevelList.length == this.ageLevelList.length ? null : this.formatObj.yearLevelList)
      .subscribe(serviceYearMembers => {

        this.AllServiceYearMembers = serviceYearMembers;
        this.serviceYearMembers = [];
        serviceYearMembers.map(a => this.serviceYearMembers.push({
          displayName: `${a.member.displayName} - ${a.yearLevel.displayName}`, 
          reference: this.databaseService.doc(`serviceYearMembers/${a.id}`).ref
        }));

        this.serviceYearMembers.sort(this.arrayService.orderByDisplayName);

        if (this.isLoading) {
          var performanceMembers = this.performance.memberList.slice();
          this.performance.memberList = [];
          performanceMembers.forEach(member => 
            this.performance.memberList.push(this.serviceYearMembers.find(m => m.reference.id === member.reference.id)));
        }

        this.isLoading = false;
      });
  }

  public onMemberListUpdated = (): void => {
    this.areMembersAccepted = false;
    
    this.theatrePerformanceService.getChurchCategoryPerformanceList(this.performance.church, this.performance.category).subscribe((performances: TheatrePerformance[]) => {

        if (performances?.length > 0) {
          
          for (var performance of performances) {
            if (performance.id === this.performance.id)
              continue;
               
            var members = performance.memberList;

            for (var pMember of this.performance.memberList) {

              if (members.find(m => m.reference.id == pMember.reference.id)) {
                this.showErrorMessage(
                  'Cannot Add Member', 
                  `Sorry, you cannot add member ${pMember.displayName}, member is already in another performance`, false);
                return;
              }
            };         
          };
        }

        this.areMembersAccepted = true;
      })
  }

  public onFileChanged = async (event): Promise<void> => {

    let uploadedFiles = event.target.files;

    if (uploadedFiles?.length > 0) {
      const uploadedFile = uploadedFiles[0];

      var fileReader = new FileReader();

      fileReader.onload = async (e) => {
        
        this.isUploadingFile = true;
        
        (await this.fileService.uploadPdfFile(
          uploadedFile,
          this.theatrePerformanceService.collection,
          'theatre-lyrics',
          this.performance.id)).subscribe( fileUrl => {
            
            this.performance.fileUrl = fileUrl;
            this.performance.fileName = uploadedFile.name;
            this.isUploadedFileChanged = true;
            this.isUploadingFile = false;

          });
      }

      fileReader.readAsText(uploadedFile);
    }

  }

  public onRemoveFile = (): void => {
    this.performance.fileUrl = null;
    this.performance.fileName = null;
  }

  public onClearUrlLink = (): void => {
    this.performance.linkUrl = null;
  }

  public validateAndEditPerformance = async (): Promise<void> => {
    if (this.performanceForm.invalid) {
      return;
    }

    const membersCount = this.performance.memberList.length;
    if (this.formatObj.areMembersRequiredOnCreation && this.formatObj.hasMinMembersLimit && membersCount < this.formatObj.minMembers) {
      this.showErrorMessage(
        'Cannot Create Performance', 
        `Sorry, you cannot edit this performance, # Members cannot be less than ${this.formatObj.minMembers}`, false)
      return;
    }

    if (this.formatObj.hasMaxMembersLimit && membersCount > this.formatObj.maxMembers) {
      this.showErrorMessage(
        'Cannot Create Performance', 
        `Sorry, you cannot edit this performance, # Members cannot be more than ${this.formatObj.maxMembers}`, false)
      return;
    }

    if (this.formatObj.hasApproval && this.performance.approver.reference.id == this.currentUser.id && !this.performance.isApprovalConfirmed) {
      this.showErrorMessage(
        'Cannot Create Performance', 
        `Sorry, you must confirm the church priest approval for this theatre submisstion`, false)
      return;
    }

    await this.editTheatrePerformance();
  }

  public editTheatrePerformance = async (hardcodedStatus?: TheatreStatus) => {
    
    var newStatus: TheatreStatus = hardcodedStatus ?? this.theatrePerformanceService.calculatePerformanceStatus(this.performance, this.formatObj);
    var existingStatus: TheatreStatus = TheatreStatus[this.performance.status.value];
    
    this.performance.isMemberFlagUpdated = false;
    this.performance.status = this.statusList.find(s => s.value == this.theatrePerformanceService.getStatusValue(newStatus));

    await this.documentService.updateDocument(this.theatrePerformanceService.collection, this.performance);

    await this.emailService.triggerTheatreEmail(this.performance, newStatus, existingStatus);

    alert('Theatre Performance Successfully Updated');

    this.dismissModal();
  }

  public acceptPerformance = async () => { 
    await this.editTheatrePerformance(TheatreStatus.SubmissionCompleted);
  }

  public declinePerformance = async () => { 
    if (!this.performance.reviewFeedback) {
      this.showErrorMessage(
        'Missing Review Feedback', 
        `You must enter feedback message before sending performance back for review`, false)
      return;
    }

    await this.editTheatrePerformance(TheatreStatus.ScriptReviewRequired);
  }

  private getStatusValue = (statusName: string): string => {
    return this.statusList.find(s => s.name == statusName).value;
  }

  dismissModal() {
    this.modalController.dismiss();
  }
}
